import { formatAuthError } from "../../localization/authErrors";
import { translate, translateRich, useLocale } from "../../localization";
import React, { useState } from "react";
import { Alert, Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import AuthForm from "../../components/AuthForm"; // Reusable form component
import { signIn, confirmSignIn } from "aws-amplify/auth"; // Import only the necessary functions
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import backgroundImage from "../../images/Sign-In-Background.jpg";
import { AppButton } from "../../components/ui";
import AuthSplitPage from "./AuthSplitPage";

const normalizeEmail = (value) => value.trim().toLowerCase();

const LoginPage = () => {
  useLocale();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const returnLocation = location.state?.from;
  const from = returnLocation?.pathname?.startsWith('/') && !returnLocation.pathname.startsWith('//')
    ? `${returnLocation.pathname}${returnLocation.search || ''}${returnLocation.hash || ''}` : '/';
  const accountMessage = location.state?.accountMessage || null;

  const handleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const normalizedEmail = normalizeEmail(email);
      const response = await signIn({
        username: normalizedEmail,
        password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH", // Explicit auth flow
        },
      });

      if (
        response?.nextStep?.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setIsNewPasswordRequired(true);
      } else {
        console.log("User signed in successfully:", response);
        navigate(from); // Redirect after successful login
      }
    } catch (err) {
      console.error("Error during sign-in:", err);
      setError(formatAuthError(err, translate("An unexpected error occurred.")));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNewPassword = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await confirmSignIn({
        challengeResponse: newPassword,
      });
      console.log("Password updated successfully:", response);
      navigate(from); // Redirect after successful password update
    } catch (err) {
      console.error("Error setting new password:", err);
      setError(formatAuthError(err, translate("An unexpected error occurred.")));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSplitPage
      heroBackgroundImage={backgroundImage}
      heroCardMaxWidth={600}
      formFirstOnMobile={false}
      hero={
        <Box>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Typography variant="h1" component="h1" gutterBottom>{translate("Welcome back to 10k Science!")}</Typography>
            <Typography variant="body1" gutterBottom>{translate("Access your account and continue your journey.")}</Typography>
          </Stack>
          <Typography variant="body2" color="textSecondary" sx={{ mt: { xs: 2, sm: 3 } }}>{translateRich("If you're new here, {value0} and get started for free.", {
  value0: <a href="/signup">{translate("sign up")}</a>
})}</Typography>
        </Box>
      }
      form={
        <Box sx={{ display: "grid", gap: 2 }}>
          {accountMessage && <Alert severity="success">{accountMessage}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {!isNewPasswordRequired ? (
            <>
              <AuthForm
                title={translate("Sign In")}
                submitLabel={translate("Sign In")}
                maxWidth={400}
                fields={[
                  {
                    label: translate("Email"),
                    type: "text",
                    value: email,
                    onChange: (e) => setEmail(e.target.value),
                  },
                  {
                    label: translate("Password"),
                    type: "password",
                    value: password,
                    onChange: (e) => setPassword(e.target.value),
                  },
                ]}
                onSubmit={handleSignIn}
                isLoading={isLoading}
                footerAction={
                  <AppButton
                    kind="ghost"
                    onClick={() => navigate("/reset-password")}
                  >{translate("Forgot Password?")}</AppButton>
                }
              />
              <Typography variant="body2" color="text.secondary" align="center">{translateRich("Don't have an account? {value0}", {
  value0: <MuiLink component={RouterLink} to="/signup" underline="hover" sx={{
    fontWeight: 600,
    color: "var(--color-action-primary, #239fc6)"
  }}>{translate("Sign up")}</MuiLink>
})}</Typography>
            </>
          ) : (
            <AuthForm
              title={translate("Set New Password")}
              submitLabel={translate("Update Password")}
              maxWidth={400}
              fields={[
                {
                  label: translate("New Password"),
                  type: "password",
                  value: newPassword,
                  onChange: (e) => setNewPassword(e.target.value),
                },
              ]}
              onSubmit={handleSetNewPassword}
              isLoading={isLoading}
            />
          )}
        </Box>
      }
      heroPanelSx={{
        backgroundAttachment: { xs: "scroll", lg: "fixed" },
      }}
      formContentMaxWidth={400}
    />
  );
};

export default LoginPage;
